import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './sticky.scss'
import classnames from 'classnames'
import { observer } from 'mobx-react'
import { AppService } from '../../services'
import { useService } from '@pushly/aqe/lib/hooks'

interface IProps {
    threshold: number
    children?: ReactNode
    getContainer?(): HTMLElement | null
}

const BASE_CLASSNAME = 'sticky'

export const Sticky = observer(({ threshold, getContainer, children }: IProps) => {
    const appSvc = useService(AppService)

    const scrollListenerSet = useRef(false)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const stickyRef = useRef<HTMLDivElement>(null)

    const [enabled, setEnabled] = useState(false)
    const [stickyMetrics, setStickyMetrics] = useState<DOMRect | null>(null)

    const appContainer = useMemo(() => {
        return getContainer?.() ?? appSvc.getContentContainer() ?? null
    }, [getContainer, appSvc.contentContainerRef])

    const handleScrollEvent = useCallback(() => {
        if (!wrapperRef.current || !stickyRef.current || !appContainer) return

        const rect = wrapperRef.current.getBoundingClientRect()
        const top = rect.top
        const isEnabled = top <= threshold

        setEnabled(isEnabled)
        setStickyMetrics(isEnabled ? rect : null)
    }, [threshold, appContainer, setEnabled, setStickyMetrics])

    useEffect(() => {
        if (!appContainer || scrollListenerSet.current) return
        appContainer.addEventListener('scroll', handleScrollEvent)
        scrollListenerSet.current = true

        return () => {
            if (!appContainer || !scrollListenerSet.current) return
            appContainer.removeEventListener('scroll', handleScrollEvent)
            scrollListenerSet.current = false
        }
    }, [appContainer, handleScrollEvent])

    const stickyStyle =
        enabled && stickyMetrics
            ? {
                  top: threshold,
                  width: stickyMetrics.width,
              }
            : undefined

    return (
        <div
            ref={wrapperRef}
            className={classnames(`${BASE_CLASSNAME}-component-wrapper`, enabled && `${BASE_CLASSNAME}-wrapper`)}
        >
            <div
                ref={stickyRef}
                className={classnames(`${BASE_CLASSNAME}-component`, enabled && BASE_CLASSNAME)}
                style={stickyStyle}
            >
                {children}
            </div>
        </div>
    )
})
